import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { UsersActionToolbar } from '../../components/admin/UsersActionToolbar';
import { CreateOwnerUserModal } from '../../components/admin/CreateOwnerUserModal';
import { EditUserModal } from '../../components/admin/EditUserModal';
import {
  AdminFilterSearch,
  AdminFilterSelect,
  AdminFiltersPanel,
} from '../../components/admin/AdminDataFilters';
import LoadingState from '../../components/common/LoadingState';
import { useConfirm } from '../../components/confirm/ConfirmDialogProvider';
import { useAuth } from '../../context/AuthContext';
import * as XLSX from 'xlsx';
import { 
  Search,
  Edit, 
  Eye, 
  ChevronLeft, 
  ChevronRight,
  AlertCircle,
  UserX,
  Download
} from 'lucide-react';
import api from '../../services/api';
import { notifyError, notifyWarning } from '../../utils/notificationCenter';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
  managedComplexId?: string;
  managedComplexName?: string;
  assignedComplexes?: Array<{ id: string; name: string }>;
  profile?: {
    apartment?: string;
    complex?: string;
    coefficient?: number | string;
    phone?: string;
    complexId?: string;
    document?: string;
  };
}

interface ResidentialComplex {
  id: string;
  name: string;
  address: string;
}

const countApartments = (apartmentValue?: string): number => {
  if (!apartmentValue) return 0;

  return apartmentValue
    .split(',')
    .map((apartment) => apartment.trim())
    .filter(Boolean).length;
};

const getNumericCoefficient = (coefficient?: number | string): number => {
  if (coefficient === null || coefficient === undefined) return 0;

  if (typeof coefficient === 'number') {
    return Number.isNaN(coefficient) ? 0 : coefficient;
  }

  if (typeof coefficient === 'string') {
    const normalized = coefficient.trim().replace(',', '.');
    const parsed = Number(normalized);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  return 0;
};

const formatCoefficient = (value: number): string => {
  return value.toLocaleString('es-CO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 6,
  });
};

const splitTowerAndApartment = (apartmentValue?: string): { tower: string; apartment: string } => {
  if (!apartmentValue) {
    return { tower: '', apartment: '' };
  }

  const normalized = apartmentValue.split(',')[0]?.trim() || '';
  if (!normalized) {
    return { tower: '', apartment: '' };
  }

  const separatorIndex = normalized.indexOf('-');
  if (separatorIndex <= 0) {
    return { tower: '', apartment: normalized };
  }

  return {
    tower: normalized.slice(0, separatorIndex).trim(),
    apartment: normalized.slice(separatorIndex + 1).trim(),
  };
};

const parseApartments = (apartmentValue?: string): string[] => {
  if (!apartmentValue) return [];
  return apartmentValue
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

const AdminUsersPage: React.FC = () => {
  const { confirm } = useConfirm();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<string>('all');
  const [filterComplex, setFilterComplex] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | 'all'>('active');
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [resendingEmails, setResendingEmails] = useState(false);
  const [resendMessage, setResendMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportScope, setExportScope] = useState<'filtered' | 'all' | 'selected'>('filtered');
  const [complexes, setComplexes] = useState<ResidentialComplex[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [usersPerPage, setUsersPerPage] = useState(25);

  // Cargar usuarios
  useEffect(() => {
    loadUsers();
    loadComplexes();
  }, []);

  const loadComplexes = async () => {
    try {
      const response = await api.api.get<{ success: boolean; data: ResidentialComplex[] }>('/complexes');
      const payload = (response as any).data;
      const list = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
      setComplexes(list);
    } catch (err) {
      console.error('Error cargando conjuntos:', err);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.api.get<User[]>('/users');
      setUsers(response.data);
    } catch (err: any) {
      console.error('Error cargando usuarios:', err);
      setError(err.response?.data?.message || 'Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmails = useCallback(async () => {
    const shouldResend = await confirm({
      title: 'Reenviar emails',
      message: '¿Reenviar emails de bienvenida a usuarios que no los recibieron?',
      confirmText: 'Reenviar',
      cancelText: 'Cancelar',
      variant: 'info',
    });

    if (!shouldResend) {
      return;
    }

    try {
      setResendingEmails(true);
      setResendMessage(null);
      
      const response = await api.api.post('/users/resend-welcome-emails');
      
      setResendMessage({
        type: 'success',
        text: response.data.message || `Emails enviados: ${response.data.sent}/${response.data.sent + response.data.failed}`
      });
      
      setTimeout(() => setResendMessage(null), 5000);
    } catch (err: any) {
      console.error('Error reenviando emails:', err);
      setResendMessage({
        type: 'error',
        text: err.response?.data?.message || 'Error al reenviar emails'
      });
    } finally {
      setResendingEmails(false);
    }
  }, [confirm]);

  const handleOpenExport = useCallback(() => {
    setExportScope(selectedUsers.size > 0 ? 'selected' : 'filtered');
    setShowExportModal(true);
  }, [selectedUsers.size]);

  const handleOpenCreateModal = useCallback(() => {
    setShowCreateModal(true);
  }, []);

  const buildImportFormatRows = (usersToExport: User[]) => {
    return usersToExport.flatMap((user) => {
      const apartments = parseApartments(user.profile?.apartment);
      const totalCoefficient = getNumericCoefficient(user.profile?.coefficient);

      if (apartments.length === 0) {
        return [{
          Torre: '',
          Apartamento: '',
          Nombre: user.firstName || '',
          Apellido: user.lastName || '',
          Teléfono: user.profile?.phone || '',
          Correo: user.email || '',
          Coeficiente: totalCoefficient,
          Documento: user.profile?.document || '',
        }];
      }

      const coefficientPerApartment = apartments.length > 0
        ? Number((totalCoefficient / apartments.length).toFixed(11))
        : totalCoefficient;

      return apartments.map((apartmentRaw) => {
        const { tower, apartment } = splitTowerAndApartment(apartmentRaw);
        return {
          Torre: tower,
          Apartamento: apartment,
          Nombre: user.firstName || '',
          Apellido: user.lastName || '',
          Teléfono: user.profile?.phone || '',
          Correo: user.email || '',
          Coeficiente: coefficientPerApartment,
          Documento: user.profile?.document || '',
        };
      });
    });
  };

  const getUsersForExport = (): User[] => {
    if (exportScope === 'all') {
      return users;
    }

    if (exportScope === 'selected') {
      return users.filter((user) => selectedUsers.has(user.id));
    }

    return filteredUsers;
  };

  const handleExportUsers = () => {
    try {
      const usersToExport = getUsersForExport();
      if (usersToExport.length === 0) {
        notifyWarning('No hay usuarios para exportar con la opción seleccionada');
        return;
      }

      const rows = buildImportFormatRows(usersToExport);

      const worksheet = XLSX.utils.json_to_sheet(rows);
      worksheet['!cols'] = [
        { wch: 10 },
        { wch: 14 },
        { wch: 20 },
        { wch: 20 },
        { wch: 15 },
        { wch: 30 },
        { wch: 12 },
        { wch: 18 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Usuarios');

      const now = new Date();
      const timestamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
      const scopeSuffix =
        exportScope === 'all' ? 'todos' : exportScope === 'selected' ? 'seleccionados' : 'filtrados';
      XLSX.writeFile(workbook, `usuarios_import_format_${scopeSuffix}_${timestamp}.xlsx`);
      setShowExportModal(false);
    } catch (err) {
      console.error('Error exportando usuarios a Excel:', err);
      notifyError('No se pudo generar el archivo Excel');
    }
  };

  const openViewUser = (user: User) => {
    setSelectedUser(user);
    setShowViewModal(true);
  };

  const openEditUser = (user: User) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleDeleteUser = async (userId: string) => {
    const shouldDeactivate = await confirm({
      title: 'Desactivar usuario',
      message: '¿Desactivar este usuario? Esta acción deshabilitará su acceso.',
      confirmText: 'Desactivar',
      cancelText: 'Cancelar',
      variant: 'warning',
    });

    if (!shouldDeactivate) return;
    try {
      const response = await api.api.delete(`/users/${userId}`);
      if (response.data?.success === false) {
        notifyError(response.data?.message || 'No se pudo desactivar el usuario');
        return;
      }
      await loadUsers();
    } catch (err: any) {
      console.error('Error al desactivar usuario:', err);
      notifyError(err.response?.data?.message || 'Error al desactivar usuario');
    }
  };

  // Funciones de selección múltiple
  const toggleUserSelection = (userId: string) => {
    // No permitir seleccionar el usuario actual
    if (currentUser?.id === userId) return;
    
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const toggleSelectAll = () => {
    // Excluir usuario actual de la selección
    const selectableUsers = paginatedUsers.filter(u => u.id !== currentUser?.id);
    
    if (selectedUsers.size === selectableUsers.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(selectableUsers.map(u => u.id)));
    }
  };

  const handleBulkActivate = async () => {
    if (selectedUsers.size === 0) return;
    const shouldActivate = await confirm({
      title: 'Activar usuarios',
      message: `¿Activar ${selectedUsers.size} usuario(s)?`,
      confirmText: 'Activar',
      cancelText: 'Cancelar',
      variant: 'info',
    });
    if (!shouldActivate) return;

    try {
      const promises = Array.from(selectedUsers).map(userId =>
        api.api.put(`/users/${userId}`, { isActive: true })
      );
      await Promise.all(promises);
      await loadUsers();
      setSelectedUsers(new Set());
    } catch (err: any) {
      console.error('Error activando usuarios:', err);
      notifyError('Error al activar algunos usuarios');
    }
  };

  const handleBulkDeactivate = async () => {
    if (selectedUsers.size === 0) return;
    const shouldDeactivate = await confirm({
      title: 'Desactivar usuarios',
      message: `¿Desactivar ${selectedUsers.size} usuario(s)?`,
      confirmText: 'Desactivar',
      cancelText: 'Cancelar',
      variant: 'warning',
    });
    if (!shouldDeactivate) return;

    try {
      const promises = Array.from(selectedUsers).map(userId =>
        api.api.put(`/users/${userId}`, { isActive: false })
      );
      await Promise.all(promises);
      await loadUsers();
      setSelectedUsers(new Set());
    } catch (err: any) {
      console.error('Error desactivando usuarios:', err);
      notifyError('Error al desactivar algunos usuarios');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedUsers.size === 0) return;
    const shouldDelete = await confirm({
      title: 'Eliminar usuarios permanentemente',
      message: `¿ELIMINAR PERMANENTEMENTE ${selectedUsers.size} usuario(s) de la base de datos? Esta acción NO se puede deshacer.`,
      confirmText: 'Eliminar permanentemente',
      cancelText: 'Cancelar',
      variant: 'danger',
    });
    if (!shouldDelete) return;

    try {
      const promises = Array.from(selectedUsers).map(userId =>
        api.api.delete(`/users/${userId}/permanent`)
      );
      await Promise.all(promises);
      await loadUsers();
      setSelectedUsers(new Set());
    } catch (err: any) {
      console.error('Error eliminando usuarios:', err);
      notifyError('Error al eliminar algunos usuarios');
    }
  };

  // Filtro base (sin estado) para métricas comparables
  const baseFilteredUsers = users.filter((user) => {
    const matchesSearch =
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.profile?.apartment?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesRole = filterRole === 'all' || user.role === filterRole;
    const matchesComplex =
      filterComplex === 'all' || user.profile?.complex === filterComplex;

    return matchesSearch && matchesRole && matchesComplex;
  });

  // Aplicar filtro de estado explícito
  const filteredUsers = baseFilteredUsers.filter((user) => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'active') return user.isActive;
    return !user.isActive;
  });

  const totalApartmentsLoaded = users.reduce(
    (total, user) => total + countApartments(user.profile?.apartment),
    0
  );

  const filteredApartments = filteredUsers.reduce(
    (total, user) => total + countApartments(user.profile?.apartment),
    0
  );

  const totalCoefficientLoaded = users.reduce(
    (total, user) => total + getNumericCoefficient(user.profile?.coefficient),
    0
  );

  const filteredCoefficient = filteredUsers.reduce(
    (total, user) => total + getNumericCoefficient(user.profile?.coefficient),
    0
  );

  const baseFilteredActiveCoefficient = baseFilteredUsers
    .filter((user) => user.isActive)
    .reduce((total, user) => total + getNumericCoefficient(user.profile?.coefficient), 0);

  const baseFilteredInactiveCoefficient = baseFilteredUsers
    .filter((user) => !user.isActive)
    .reduce((total, user) => total + getNumericCoefficient(user.profile?.coefficient), 0);

  const baseFilteredTotalCoefficient = baseFilteredUsers.reduce(
    (total, user) => total + getNumericCoefficient(user.profile?.coefficient),
    0
  );

  const filteredCoefficientIsHundred = Math.abs(filteredCoefficient - 100) < 0.0001;

  // Paginación
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const startIndex = (currentPage - 1) * usersPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, startIndex + usersPerPage);

  // Obtener conjuntos únicos para filtro
  const complexNames = Array.from(
    new Set(users.map((u) => u.profile?.complex).filter(Boolean))
  ) as string[];

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-purple-100 text-purple-800';
      case 'manager':
        return 'bg-blue-100 text-blue-800';
      case 'builder':
        return 'bg-amber-100 text-amber-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':
        return 'Admin';
      case 'manager':
        return 'Manager';
      case 'builder':
        return 'Constructora';
      case 'owner':
        return 'Propietario';
      default:
        return role;
    }
  };

  const closeModals = () => {
    setShowViewModal(false);
    setShowEditModal(false);
    setSelectedUser(null);
  };

  if (loading) {
    return (
      <AdminLayout>
        <LoadingState layout="page" message="Cargando usuarios..." />
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="p-8">
          <div className="max-w-7xl mx-auto">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-red-800 mb-2">Error al cargar usuarios</h3>
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={loadUsers}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Reintentar
              </button>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6 space-y-5">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-gray-900">Gestión de Usuarios</h1>
              <p className="mt-2 text-gray-600">
                {filteredUsers.length} usuario{filteredUsers.length !== 1 ? 's' : ''} encontrado{filteredUsers.length !== 1 ? 's' : ''}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2 text-sm" role="list" aria-label="Métricas de usuarios">
              <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-gray-700">
                Aptos visibles: <span className="ml-1 font-semibold text-gray-900">{filteredApartments}</span>
              </span>
              <span className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-blue-700">
                Aptos totales: <span className="ml-1 font-semibold text-blue-900">{totalApartmentsLoaded}</span>
              </span>
              <span
                className={`inline-flex items-center rounded-full px-3 py-1 ${
                  filteredCoefficientIsHundred ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
                }`}
              >
                Coef. visible: <span className="ml-1 font-semibold">{formatCoefficient(filteredCoefficient)}</span>
              </span>
              <span className="inline-flex items-center rounded-full bg-indigo-50 px-3 py-1 text-indigo-700">
                Coef. total: <span className="ml-1 font-semibold">{formatCoefficient(totalCoefficientLoaded)}</span>
              </span>
              <span className="inline-flex items-center rounded-full bg-green-50 px-3 py-1 text-green-700">
                Coef. activos (filtro): <span className="ml-1 font-semibold">{formatCoefficient(baseFilteredActiveCoefficient)}</span>
              </span>
              <span className="inline-flex items-center rounded-full bg-rose-50 px-3 py-1 text-rose-700">
                Coef. inactivos (filtro): <span className="ml-1 font-semibold">{formatCoefficient(baseFilteredInactiveCoefficient)}</span>
              </span>
              <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-slate-700">
                Coef. total (filtro): <span className="ml-1 font-semibold">{formatCoefficient(baseFilteredTotalCoefficient)}</span>
              </span>
            </div>

            <div className="pt-1">
            <UsersActionToolbar
              onCreateUser={handleOpenCreateModal}
              onExport={handleOpenExport}
              onResendEmails={handleResendEmails}
              resendingEmails={resendingEmails}
            />
            </div>
          </div>

          {showExportModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
              <div className="w-full max-w-lg rounded-xl bg-white shadow-2xl">
                <div className="border-b border-gray-200 px-6 py-4">
                  <h3 className="text-lg font-semibold text-gray-900">Exportar usuarios a Excel</h3>
                  <p className="mt-1 text-sm text-gray-600">
                    El archivo se generará con el mismo formato de columnas usado en la importación.
                  </p>
                </div>

                <div className="space-y-3 px-6 py-5">
                  <label
                    className={`flex items-start gap-3 rounded-lg border p-3 ${
                      selectedUsers.size > 0
                        ? 'cursor-pointer border-purple-200 hover:border-purple-300 hover:bg-purple-50'
                        : 'cursor-not-allowed border-gray-200 bg-gray-50 opacity-70'
                    }`}
                  >
                    <input
                      type="radio"
                      name="export-scope"
                      checked={exportScope === 'selected'}
                      onChange={() => setExportScope('selected')}
                      disabled={selectedUsers.size === 0}
                      className="mt-1 h-4 w-4 border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Usuarios seleccionados</p>
                      <p className="text-xs text-gray-600">Se exportarán {selectedUsers.size} usuario(s) seleccionados en la tabla.</p>
                    </div>
                  </label>

                  <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 p-3 hover:border-emerald-300 hover:bg-emerald-50">
                    <input
                      type="radio"
                      name="export-scope"
                      checked={exportScope === 'filtered'}
                      onChange={() => setExportScope('filtered')}
                      className="mt-1 h-4 w-4 border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Solo usuarios filtrados</p>
                      <p className="text-xs text-gray-600">Se exportarán {filteredUsers.length} usuario(s) según los filtros actuales.</p>
                    </div>
                  </label>

                  <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 p-3 hover:border-blue-300 hover:bg-blue-50">
                    <input
                      type="radio"
                      name="export-scope"
                      checked={exportScope === 'all'}
                      onChange={() => setExportScope('all')}
                      className="mt-1 h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Todos los usuarios</p>
                      <p className="text-xs text-gray-600">Se exportarán {users.length} usuario(s) sin aplicar filtros.</p>
                    </div>
                  </label>
                </div>

                <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
                  <button
                    onClick={() => setShowExportModal(false)}
                    className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleExportUsers}
                    disabled={getUsersForExport().length === 0}
                    className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-gray-400"
                  >
                    <Download size={16} />
                    Descargar Excel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Mensaje de resultado */}
          {resendMessage && (
            <div className={`mb-4 p-4 rounded-lg ${
              resendMessage.type === 'success' 
                ? 'bg-green-50 border border-green-200 text-green-800' 
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
              {resendMessage.text}
            </div>
          )}

          {/* Filters */}
          <AdminFiltersPanel columns={5}>
            <AdminFilterSearch
              value={searchTerm}
              onChange={(value) => {
                setSearchTerm(value);
                setCurrentPage(1);
              }}
              placeholder="Buscar por nombre, email o apartamento..."
            />

            <AdminFilterSelect
              value={filterRole}
              onChange={(value) => {
                setFilterRole(value);
                setCurrentPage(1);
              }}
              aria-label="Filtrar por rol"
            >
              <option value="all">Todos los roles</option>
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
              <option value="builder">Constructora</option>
              <option value="owner">Propietario</option>
            </AdminFilterSelect>

            <AdminFilterSelect
              value={filterComplex}
              onChange={(value) => {
                setFilterComplex(value);
                setCurrentPage(1);
              }}
              aria-label="Filtrar por conjunto"
            >
              <option value="all">Todos los conjuntos</option>
              {complexNames.map((complex) => (
                <option key={complex} value={complex}>
                  {complex}
                </option>
              ))}
            </AdminFilterSelect>

            <AdminFilterSelect
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value as 'active' | 'inactive' | 'all');
                setCurrentPage(1);
              }}
              aria-label="Filtrar por estado"
            >
              <option value="active">Solo activos</option>
              <option value="inactive">Solo inactivos</option>
              <option value="all">Todos los usuarios</option>
            </AdminFilterSelect>

            <AdminFilterSelect
              value={usersPerPage}
              onChange={(value) => {
                setUsersPerPage(Number(value));
                setCurrentPage(1);
              }}
              aria-label="Resultados por página"
            >
              <option value={10}>10 por página</option>
              <option value={25}>25 por página</option>
              <option value={50}>50 por página</option>
              <option value={100}>100 por página</option>
            </AdminFilterSelect>
          </AdminFiltersPanel>

          {/* Users Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {/* Barra de acciones masivas */}
            {selectedUsers.size > 0 && (
              <div className="bg-blue-50 border-b border-blue-200 px-6 py-3 flex items-center justify-between">
                <span className="text-sm font-medium text-blue-700">
                  {selectedUsers.size} usuario(s) seleccionado(s)
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={handleBulkActivate}
                    className="px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                  >
                    Activar
                  </button>
                  <button
                    onClick={handleBulkDeactivate}
                    className="px-3 py-1.5 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700"
                  >
                    Desactivar
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    className="px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                  >
                    Eliminar
                  </button>
                  <button
                    onClick={() => setSelectedUsers(new Set())}
                    className="px-3 py-1.5 bg-gray-500 text-white text-sm rounded hover:bg-gray-600"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={paginatedUsers.length > 0 && paginatedUsers.filter(u => u.id !== currentUser?.id).length > 0 && selectedUsers.size === paginatedUsers.filter(u => u.id !== currentUser?.id).length}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Usuario
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Apartamento(s)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Conjunto
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Coeficiente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rol
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedUsers.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-12 text-center">
                        <div className="text-gray-500">
                          <Search className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                          <p className="text-lg font-medium">No se encontraron usuarios</p>
                          <p className="text-sm">Intenta ajustar los filtros de búsqueda</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paginatedUsers.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedUsers.has(user.id)}
                            onChange={() => toggleUserSelection(user.id)}
                            disabled={currentUser?.id === user.id}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                            title={currentUser?.id === user.id ? 'No puedes seleccionar tu propia cuenta' : ''}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {user.firstName} {user.lastName}
                            </div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div
                            className="max-w-[260px] truncate text-sm text-gray-900"
                            title={user.profile?.apartment || '-'}
                          >
                            {user.profile?.apartment || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {user.profile?.complex || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {user.profile?.coefficient ? Number(user.profile.coefficient).toFixed(10) : '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeColor(
                              user.role
                            )}`}
                          >
                            {getRoleLabel(user.role)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              user.isActive
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {user.isActive ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => openViewUser(user)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Ver detalles"
                            >
                              <Eye size={18} />
                            </button>
                            <button
                              onClick={() => openEditUser(user)}
                              className="text-gray-600 hover:text-gray-900"
                              title="Editar"
                            >
                              <Edit size={18} />
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user.id)}
                              className="text-orange-600 hover:text-orange-900"
                              title="Desactivar"
                            >
                              <UserX size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="border-t border-gray-200 bg-white px-6 py-5">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="text-sm text-gray-600">
                  <p className="font-medium text-gray-900">
                    Total: <span className="text-lg text-blue-600">{filteredUsers.length}</span> usuarios
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Mostrando <span className="font-semibold">{startIndex + 1}</span> a{' '}
                    <span className="font-semibold">
                      {Math.min(startIndex + usersPerPage, filteredUsers.length)}
                    </span>{' '}
                    (Página <span className="font-semibold">{currentPage}</span> de{' '}
                    <span className="font-semibold">{totalPages || 1}</span>)
                  </p>
                </div>

                {totalPages > 1 && (
                  <div className="flex flex-wrap items-center justify-center gap-2">
                    <button
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      title="Primera página"
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-blue-50 hover:border-blue-300 disabled:opacity-40 disabled:cursor-not-allowed transition duration-150"
                    >
                      ⟨⟨
                    </button>
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-blue-50 hover:border-blue-300 disabled:opacity-40 disabled:cursor-not-allowed transition duration-150"
                    >
                      <ChevronLeft size={18} />
                    </button>

                    {/* Números de página con elipsis */}
                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter((page) => {
                          return (
                            page === 1 ||
                            page === totalPages ||
                            Math.abs(page - currentPage) <= 1
                          );
                        })
                        .map((page, idx, arr) => (
                          <React.Fragment key={page}>
                            {idx > 0 && arr[idx - 1] !== page - 1 && (
                              <span className="px-2 text-gray-400">…</span>
                            )}
                            <button
                              onClick={() => setCurrentPage(page)}
                              className={`px-3 py-2 text-sm font-medium rounded-lg transition duration-150 ${
                                currentPage === page
                                  ? 'bg-blue-600 text-white shadow-md'
                                  : 'text-gray-700 hover:bg-gray-100 border border-gray-300'
                              }`}
                            >
                              {page}
                            </button>
                          </React.Fragment>
                        ))}
                    </div>

                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-blue-50 hover:border-blue-300 disabled:opacity-40 disabled:cursor-not-allowed transition duration-150"
                    >
                      <ChevronRight size={18} />
                    </button>
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      title="Última página"
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-blue-50 hover:border-blue-300 disabled:opacity-40 disabled:cursor-not-allowed transition duration-150"
                    >
                      ⟩⟩
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Ver Usuario */}
      {showViewModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[85vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Detalles del usuario</h3>
              <button onClick={closeModals} className="text-gray-400 hover:text-gray-600">
                ×
              </button>
            </div>
            <div className="p-4 space-y-3 text-sm text-gray-800 overflow-y-auto max-h-[calc(85vh-132px)]">
              <div>
                <p className="font-semibold">Nombre</p>
                <p>{selectedUser.firstName} {selectedUser.lastName}</p>
              </div>
              <div>
                <p className="font-semibold">Email</p>
                <p>{selectedUser.email}</p>
              </div>
              <div className="flex gap-4">
                <div>
                  <p className="font-semibold">Rol</p>
                  <p>{getRoleLabel(selectedUser.role)}</p>
                </div>
                <div>
                  <p className="font-semibold">Estado</p>
                  <p className={selectedUser.isActive ? 'text-green-600' : 'text-red-600'}>
                    {selectedUser.isActive ? 'Activo' : 'Inactivo'}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="font-semibold">Apartamento(s)</p>
                  <div className="mt-1 max-h-28 overflow-y-auto rounded border border-gray-100 bg-gray-50 p-2 text-xs leading-5 break-words whitespace-normal">
                    {selectedUser.profile?.apartment || '-'}
                  </div>
                </div>
                <div>
                  <p className="font-semibold">Conjunto</p>
                  <p>{selectedUser.profile?.complex || '-'}</p>
                </div>
                <div>
                  <p className="font-semibold">Coeficiente</p>
                  <p>{selectedUser.profile?.coefficient ? Number(selectedUser.profile.coefficient).toFixed(10) : '-'}</p>
                </div>
                <div>
                  <p className="font-semibold">Teléfono</p>
                  <p>{selectedUser.profile?.phone || '-'}</p>
                </div>
                <div>
                  <p className="font-semibold">Documento</p>
                  <p>{selectedUser.profile?.document || '-'}</p>
                </div>
                {selectedUser.role === 'builder' ? (
                  <div className="col-span-2">
                    <p className="font-semibold">Conjuntos asignados</p>
                    <p>
                      {(selectedUser.assignedComplexes || []).map((item) => item.name).join(', ') || '-'}
                    </p>
                  </div>
                ) : null}
                {selectedUser.role === 'manager' ? (
                  <div className="col-span-2">
                    <p className="font-semibold">Conjunto administrado</p>
                    <p>{selectedUser.managedComplexName || '-'}</p>
                  </div>
                ) : null}
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={closeModals}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && selectedUser && (
        <EditUserModal
          user={selectedUser}
          complexes={complexes}
          onClose={closeModals}
          onSaved={loadUsers}
        />
      )}

      {showCreateModal && (
        <CreateOwnerUserModal
          complexes={complexes}
          onClose={() => setShowCreateModal(false)}
          onCreated={loadUsers}
        />
      )}
    </AdminLayout>
  );
};

export default AdminUsersPage;
